/**
 * Admin Dashboard - Main dashboard with KPIs, statistics, quick actions
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/AppContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Settings, LogOut, Search, TrendingUp, User, Briefcase,
  CreditCard, Activity, ShieldCheck, MessageSquare, Bell, ClipboardCheck,
  Server, Loader2, UserCheck, UserPlus, DollarSign, CheckCircle, Mail, Gift,
  LayoutDashboard, BarChart3, Megaphone, Image, ChevronRight, Menu, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SidebarItem = ({ icon: Icon, label, path, badge, navigate, active, scrollTo }) => (
  <button
    onClick={() => scrollTo ? document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'start' }) : navigate(path)}
    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
      active ? 'bg-[#0044CC] text-white' : 'text-gray-600 hover:bg-gray-100'
    }`}
    data-testid={`sidebar-${label.toLowerCase().replace(/\s/g,'-')}`}
  >
    <span className="flex items-center gap-2">
      <Icon className="w-4 h-4" />
      {label}
    </span>
    {badge > 0 && (
      <Badge className={`text-xs px-1.5 ${active ? 'bg-white text-[#0044CC]' : 'bg-red-500 text-white'}`}>
        {badge}
      </Badge>
    )}
  </button>
);

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage: changeLanguage } = useLanguage();
  const [stats, setStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('users');
  const [searchResults, setSearchResults] = useState([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchRecentActivities();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin-Zugang erforderlich');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    const res = await axios.get(`${API}/admin/recent-activities`, { withCredentials: true }).catch(console.error);
    if (res) setRecentActivities(res.data);
  };

  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    try {
      if (searchType === 'users') {
        const res = await axios.get(`${API}/admin/users?search=${q}`, { withCredentials: true });
        setSearchResults(res.data.users);
      } else {
        const res = await axios.get(`${API}/admin/jobs?search=${q}`, { withCredentials: true });
        setSearchResults(res.data.jobs);
      }
    } catch {
      toast.error('Suche fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen infirmly-dark flex items-center justify-center bg-gray-100" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  const pendingVerif = stats?.subscriptions?.pending_verifications || 0;

  return (
    <div className="min-h-screen infirmly-dark flex" data-testid="admin-dashboard" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }}>
      {/* Mobile overlay */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setMobileNavOpen(false)} />
      )}

      {/* Sidebar - hidden on mobile, slides in via hamburger */}
      <aside className={`fixed md:sticky top-0 left-0 z-50 w-64 md:w-56 border-r border-white/10 bg-[#0A1A3E] md:bg-white/[0.04] flex-shrink-0 flex flex-col h-screen overflow-y-auto transform transition-transform duration-200 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-bold text-lg text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Admin
          </h2>
          <button onClick={() => setMobileNavOpen(false)} className="md:hidden w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">{t('adminNavDashboard')}</p>
          <SidebarItem icon={LayoutDashboard} label={t('adminNavOverview')} path="/admin" navigate={navigate} active={location.pathname === '/admin'} />
          <SidebarItem icon={BarChart3} label={t('adminNavAnalytics')} path="/admin" navigate={navigate} active={false} scrollTo="section-analytics" />
          <SidebarItem icon={User} label={t('adminNavUsers')} path="/admin/users" navigate={navigate} active={location.pathname === '/admin/users'} />
          <SidebarItem icon={Briefcase} label={t('adminNavJobs')} path="/admin/jobs" navigate={navigate} active={location.pathname === '/admin/jobs'} />
          <SidebarItem icon={CreditCard} label={t('adminNavSubscriptions')} path="/admin/payments" navigate={navigate} active={location.pathname === '/admin/payments'} />
          <SidebarItem icon={Activity} label={t('adminNavUserActivity')} path="/admin/user-activity" navigate={navigate} active={location.pathname === '/admin/user-activity'} />
          <SidebarItem icon={TrendingUp} label={t('adminNavConversion')} path="/admin" navigate={navigate} active={false} scrollTo="section-conversion" />
          <SidebarItem icon={Activity} label={t('adminNavQuickActions')} path="/admin" navigate={navigate} active={false} scrollTo="section-quick-actions" />

          <Separator className="my-2" />
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">{t('adminNavManagement')}</p>
          <SidebarItem icon={ShieldCheck} label={t('adminNavVerifications')} path="/admin/users?filter=pending_verification" navigate={navigate} active={false} badge={pendingVerif} />
          <SidebarItem icon={UserCheck} label={t('adminNavUserMgmt')} path="/admin/users" navigate={navigate} active={false} />
          <SidebarItem icon={Briefcase} label={t('adminNavJobMgmt')} path="/admin/jobs" navigate={navigate} active={location.pathname === '/admin/jobs'} />
          <SidebarItem icon={MessageSquare} label={t('adminNavSupport')} path="/admin/support" navigate={navigate} active={location.pathname === '/admin/support'} />
          <SidebarItem icon={Megaphone} label={t('adminNavAnnouncements')} path="/admin/announcements" navigate={navigate} active={location.pathname === '/admin/announcements'} />
          <SidebarItem icon={Image} label={t('adminNavAds')} path="/admin/ads" navigate={navigate} active={location.pathname === '/admin/ads'} />
          <SidebarItem icon={Image} label={t('adminNavHeroSlides')} path="/admin/hero-slides" navigate={navigate} active={location.pathname === '/admin/hero-slides'} />
          <SidebarItem icon={ClipboardCheck} label={t('adminNavAuditLogs')} path="/admin/audit-logs" navigate={navigate} active={location.pathname === '/admin/audit-logs'} />
        </nav>

        <div className="p-3 border-t space-y-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/settings')} className="w-full justify-start rounded-lg text-sm">
            <Settings className="w-4 h-4 mr-2" /> {t('settings')}
          </Button>
          <Button variant="outline" size="sm" onClick={onLogout} className="w-full justify-start rounded-lg text-sm text-red-600 hover:text-red-700 hover:bg-red-50">
            <LogOut className="w-4 h-4 mr-2" /> {t('logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <header className="border-b border-white/10 sticky bg-white/[0.04] backdrop-blur top-0 z-30">
          <div className="px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileNavOpen(true)}
                className="md:hidden w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"
                data-testid="admin-mobile-menu"
              >
                <Menu className="w-5 h-5 text-white" />
              </button>
              <h1 className="text-lg md:text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {t('adminDashboard')}
              </h1>
            </div>
            <div className="flex items-center gap-2 md:gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t('adminSearch')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 rounded-full w-full sm:w-64"
                  data-testid="admin-search-input"
                />
              </div>
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="w-24 sm:w-28 rounded-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="users">{t('adminSearchUsers')}</SelectItem>
                  <SelectItem value="jobs">{t('adminSearchJobs')}</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} className="bg-[#0044CC] hover:bg-[#003399] rounded-full">
                {t('adminSearchBtn')}
              </Button>
              <Select value={language} onValueChange={changeLanguage}>
                <SelectTrigger className="w-20 rounded-full" data-testid="admin-language-select">
                  <SelectValue>{language.toUpperCase()}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="de">DE</SelectItem>
                  <SelectItem value="en">EN</SelectItem>
                  <SelectItem value="fr">FR</SelectItem>
                  <SelectItem value="it">IT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {searchResults.length > 0 && (
            <div className="px-6 pb-4">
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {searchResults.map((item, idx) => (
                  <div key={idx} className="p-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                    onClick={() => { setSearchResults([]); navigate(item.user_id ? `/admin/users/${item.user_id}` : `/admin/jobs/${item.job_id}`); }}>
                    <div>
                      <p className="font-medium text-sm">{item.name || item.title}</p>
                      <p className="text-xs text-gray-500">{item.email || item.category}</p>
                    </div>
                    <Badge variant={item.role === 'provider' ? 'default' : 'secondary'}>
                      {item.role || item.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </header>

        <main className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* KPI Overview */}
          <div id="section-analytics" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-green-700">Total Users</span>
                  <User className="w-4 h-4 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-green-800">{stats?.users?.total || 0}</p>
                <p className="text-xs text-green-600 mt-1">+{stats?.users?.new_7d || 0} diese Woche</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-blue-700">Total Jobs</span>
                  <Briefcase className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-blue-800">{stats?.jobs?.total || 0}</p>
                <p className="text-xs text-blue-600 mt-1">{stats?.jobs?.open || 0} offen</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-purple-700">Active Subs</span>
                  <CreditCard className="w-4 h-4 text-purple-600" />
                </div>
                <p className="text-2xl font-bold text-purple-800">{stats?.subscriptions?.active || 0}</p>
                <p className="text-xs text-purple-600 mt-1">{pendingVerif} ausstehend</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-orange-700">MRR</span>
                  <TrendingUp className="w-4 h-4 text-orange-600" />
                </div>
                <p className="text-2xl font-bold text-orange-800 text-sm">CHF {stats?.revenue?.mrr?.toFixed(0) || '0'}</p>
                <p className="text-xs text-orange-600 mt-1">ARR: CHF {stats?.revenue?.arr_chf?.toFixed(0) || '0'}</p>
              </CardContent>
            </Card>
          </div>

          {/* 2nd row KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-4">
              <p className="text-sm text-gray-500">New Users (7d)</p>
              <p className="text-2xl font-bold text-[#0044CC]">{stats?.users?.new_7d || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-sm text-gray-500">New Users (30d)</p>
              <p className="text-2xl font-bold text-[#0044CC]">{stats?.users?.new_30d || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-sm text-gray-500">Pending Verif.</p>
              <p className={`text-2xl font-bold ${pendingVerif > 0 ? 'text-orange-600' : 'text-gray-700'}`}>{pendingVerif}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-sm text-gray-500">ARR</p>
              <p className="text-2xl font-bold text-green-600">CHF {((stats?.revenue?.mrr || 0) * 12).toFixed(0)}</p>
            </CardContent></Card>
          </div>

          {/* Conversion Rates */}
          <Card id="section-conversion">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4" /> Conversion Rates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-[#0044CC]">{stats?.conversion?.verification_rate}%</p>
                  <p className="text-xs text-gray-600">Verifizierungsrate</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{stats?.conversion?.job_completion}%</p>
                  <p className="text-xs text-gray-600">Auftragsabschluss</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-purple-600">{stats?.conversion?.sub_conversion}%</p>
                  <p className="text-xs text-gray-600">Abo-Conversion</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-orange-600">{stats?.conversion?.client_pro_ratio}</p>
                  <p className="text-xs text-gray-600">Kunden:Anbieter</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card id="section-quick-actions">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3">
                {[
                  { icon: ShieldCheck, label: t('verified'), path: '/admin/users?filter=pending_verification', color: 'text-green-600', badge: pendingVerif },
                  { icon: UserCheck, label: t('adminRegistrations'), path: '/admin/registrations', color: 'text-yellow-600' },
                  { icon: User, label: t('userManagement'), path: '/admin/users', color: 'text-blue-600' },
                  { icon: Briefcase, label: t('jobManagement'), path: '/admin/jobs', color: 'text-purple-600' },
                  { icon: MessageSquare, label: t('adminSupport'), path: '/admin/support', color: 'text-cyan-600' },
                  { icon: Megaphone, label: t('adminAnnouncements'), path: '/admin/announcements', color: 'text-orange-600' },
                  { icon: Image, label: t('adsMarketing'), path: '/admin/ads', color: 'text-pink-600' },
                  { icon: Image, label: 'Hero Slides', path: '/admin/hero-slides', color: 'text-fuchsia-500' },
                  { icon: CreditCard, label: t('adminPayments'), path: '/admin/payments', color: 'text-emerald-600' },
                  { icon: Bell, label: t('adminNotifications'), path: '/admin/notifications', color: 'text-orange-500' },
                  { icon: Gift, label: t('adminPromo'), path: '/admin/promo', color: 'text-purple-600' },
                  { icon: ClipboardCheck, label: t('adminAuditLogs'), path: '/admin/audit-logs', color: 'text-indigo-600' },
                  { icon: Server, label: t('adminBackup'), path: '/admin/backup', color: 'text-teal-600' },
                ].map((item, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    className="flex flex-col items-center gap-1 h-auto py-3 relative"
                    onClick={() => navigate(item.path)}
                    data-testid={`quick-action-${item.label}`}
                  >
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                    <span className="text-xs">{item.label}</span>
                    {item.badge > 0 && (
                      <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1 min-w-5 h-5">{item.badge}</Badge>
                    )}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activities */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-blue-600" /> Neue Benutzer
                  {recentActivities?.new_users_24h > 0 && (
                    <Badge className="bg-blue-500 text-white ml-2 text-xs">+{recentActivities.new_users_24h} heute</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {recentActivities?.recent_users?.slice(0, 5).map((u, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                      onClick={() => navigate('/admin/users')}>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-3 h-3 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-xs">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                        </div>
                      </div>
                      <Badge variant={u.role === 'provider' ? 'default' : 'secondary'} className="text-xs">
                        {u.role === 'provider' ? 'Anbieter' : 'Kunde'}
                      </Badge>
                    </div>
                  ))}
                  {(!recentActivities?.recent_users || recentActivities.recent_users.length === 0) && (
                    <p className="text-gray-400 text-center py-4 text-sm">Keine neuen Benutzer</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-600" /> Neue Zahlungen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {recentActivities?.recent_payments?.slice(0, 5).map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                      onClick={() => navigate('/admin/payments')}>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                          <CreditCard className="w-3 h-3 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-xs">{p.user_name || 'Unbekannt'}</p>
                          <p className="text-xs text-gray-500">{p.type?.replace('subscription_', '').toUpperCase()}</p>
                        </div>
                      </div>
                      <p className="font-bold text-green-600 text-sm">CHF {(p.amount / 100).toFixed(2)}</p>
                    </div>
                  ))}
                  {(!recentActivities?.recent_payments || recentActivities.recent_payments.length === 0) && (
                    <p className="text-gray-400 text-center py-4 text-sm">Keine neuen Zahlungen</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Check Payments + Welcome Email quick tools */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Admin Tools</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-3 flex-wrap">
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={async () => {
                  if (window.confirm('Alle ausstehenden Zahlungen prüfen und aktivieren?')) {
                    try {
                      toast.info('Prüfe Zahlungen...');
                      const res = await axios.post(`${API}/admin/check-pending-payments`, {}, { withCredentials: true });
                      toast.success(`Geprüft: ${res.data.checked}, Aktiviert: ${res.data.completed}`);
                      fetchStats();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Fehler');
                    }
                  }
                }}
                data-testid="check-payments-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Zahlungen prüfen
              </Button>
              <Button
                variant="outline"
                className="border-blue-300 text-blue-600"
                onClick={async () => {
                  const email = window.prompt('E-Mail-Adresse des Benutzers:');
                  if (!email) return;
                  const lang = window.prompt('Sprache (de/en/fr/it):', 'de');
                  try {
                    const res = await axios.post(`${API}/admin/send-welcome-email/${email}`, { language: lang || 'de' }, { withCredentials: true });
                    toast.success(res.data.message);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Fehler');
                  }
                }}
                data-testid="send-welcome-btn"
              >
                <Mail className="w-4 h-4 mr-2" /> Welcome E-Mail
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
